/**
 * Inspection Mode — §I.
 *
 * ── What this screen actually is ─────────────────────────────────────────────
 *
 * A person is standing beside a trailer in a field in Vieques, in the sun,
 * holding a phone in one hand and a flashlight in the other, with one bar of
 * signal that comes and goes. Everything below follows from that:
 *
 *  - **56px targets.** PASS / FAIL / UNKNOWN are the three things pressed a
 *    hundred times a visit. They are full-width thirds, not icons in a row.
 *  - **Nothing waits for the network.** Every tap writes to `outbox.ts` with a
 *    fresh `client_id` and the row updates immediately. `inspection_entries` has
 *    `ON CONFLICT (client_id) DO NOTHING`, so a replay is a server-side no-op
 *    and the queue can retry without knowing whether the first attempt landed.
 *  - **The visit survives a reload.** The inspection's `client_id`, and every
 *    answer given so far, live in localStorage under the opportunity. Killing
 *    the app and reopening it resumes the SAME inspection rather than starting
 *    a second one — which is the failure the server's `client_id` column exists
 *    to prevent and which this screen must not cause in the first place.
 *  - **A queued count is always on screen.** The one thing worse than an
 *    unsent entry is an unsent entry nobody knows about.
 *
 * ── Photos, and the refusal ──────────────────────────────────────────────────
 *
 * §10.3: this screen refuses to start a photo-bearing inspection when
 * `/api/health` reports no uploads bucket. It does not hide the button — a
 * hidden button is indistinguishable from a broken one — it disables it and
 * says why. Photos go through `image.ts` first (HEIC → JPEG, 2048px, EXIF gone)
 * because `uploads.ts` sniffs magic bytes and would answer 415 to the HEIC an
 * iPhone actually produces.
 *
 * ── Coordinates ──────────────────────────────────────────────────────────────
 *
 * `inspections.ts` accepts an optional inspector `lat`/`lng`. This screen sends
 * neither, on purpose: it would mean a geolocation permission prompt in the
 * middle of the one path that has to be instant, and §14.2 puts coordinates on
 * the never-logged list. If it is wanted later it belongs behind an explicit
 * "record that I was here" control, not in the start path.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { get, post } from '../lib/api'
import { useLocale, type Locale } from '../lib/i18n'
import { navigate, useRoute } from '../lib/route'
import Icon from '../components/Icon'
import * as outbox from '../lib/outbox'
import * as oppCache from '../lib/oppCache'
import * as photoQueue from '../lib/photoQueue'
import { ImagePrepError, prepareImage } from '../lib/image'

/* ────────────────────────────────────────────────────────────────────────────
 * Server shapes (read from services/api/src/diligence.ts and inspections.ts)
 * ────────────────────────────────────────────────────────────────────────── */

interface DiligenceItem {
  id: string
  item_key: string
  label_en: string
  label_es: string | null
  question_en: string | null
  question_es: string | null
  category: string | null
  blocking: number
  sort_order: number
  status: string
}

interface ChecklistResponse {
  items: DiligenceItem[]
}

interface OpportunityResponse {
  opportunity?: { id: string; ref?: string | null; title?: string | null }
}

type ItemStatus = 'pass' | 'fail' | 'unknown' | 'na'

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  title: 'Inspection',
  pass: 'PASS',
  fail: 'FAIL',
  unknown: 'UNKNOWN',
  na: 'N/A',
  note: 'Note',
  photo: 'Photo',
  blocking: 'Blocking',
  finish: 'Finish inspection',
  finishing: 'Finishing…',
  verdict: 'What is the call',
  buy: 'Buy',
  negotiate: 'Negotiate',
  walk: 'Walk',
  undecided: 'Undecided',
  summary: 'Summary (optional)',
  queued: 'waiting to send',
  allSent: 'Everything is sent',
  offline: 'Offline — everything you do here is kept on the phone.',
  notDurable: 'This browser will not keep the queue if you close the tab. Send before you close it.',
  noUploads: 'Photos are switched off on this deployment — the file store is not configured.',
  loading: 'Loading…',
  noChecklist: 'This opportunity has no checklist yet.',
  makeChecklist: 'Create the checklist',
  needSignal: 'Creating a checklist needs a connection. Open this opportunity once on Wi-Fi before you drive out.',
  cachedAt: 'Showing data from',
  offlineSuffix: '— offline',
  addNote: 'Add a note',
  save: 'Save',
  cancel: 'Cancel',
  photoWorking: 'Preparing…',
  photoBad: 'That photo could not be read.',
  entries: 'entries this visit',
  finished: 'Inspection finished.',
  back: 'Back to the opportunity',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Inspección',
  pass: 'PASA',
  fail: 'FALLA',
  unknown: 'NO SÉ',
  na: 'N/A',
  note: 'Nota',
  photo: 'Foto',
  blocking: 'Bloquea',
  finish: 'Terminar inspección',
  finishing: 'Terminando…',
  verdict: 'Cuál es la decisión',
  buy: 'Comprar',
  negotiate: 'Negociar',
  walk: 'Dejarlo',
  undecided: 'Sin decidir',
  summary: 'Resumen (opcional)',
  queued: 'esperando para enviarse',
  allSent: 'Todo se envió',
  offline: 'Sin conexión — todo lo que hagas aquí se guarda en el teléfono.',
  notDurable: 'Este navegador no va a guardar la fila si cierras la pestaña. Envía antes de cerrarla.',
  noUploads: 'Las fotos están apagadas en este despliegue — el almacén de archivos no está configurado.',
  loading: 'Cargando…',
  noChecklist: 'Esta oportunidad todavía no tiene lista.',
  makeChecklist: 'Crear la lista',
  needSignal: 'Crear una lista necesita conexión. Abre esta oportunidad en Wi-Fi antes de salir.',
  cachedAt: 'Mostrando datos de las',
  offlineSuffix: '— sin conexión',
  addNote: 'Añadir una nota',
  save: 'Guardar',
  cancel: 'Cancelar',
  photoWorking: 'Preparando…',
  photoBad: 'No se pudo leer esa foto.',
  entries: 'anotaciones en esta visita',
  finished: 'Inspección terminada.',
  back: 'Volver a la oportunidad',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/* ────────────────────────────────────────────────────────────────────────────
 * The visit, held locally
 * ────────────────────────────────────────────────────────────────────────── */

interface LocalSession {
  clientId: string
  startedAt: number
  /** What has been answered on THIS visit, so the rows are right after a reload. */
  answers: Record<string, ItemStatus>
  notes: Record<string, number>
  photos: Record<string, number>
}

const SESSION_PREFIX = 'hh.inspect.'

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch {
    /* no secure context; the fallback below is good enough for a client id */
  }
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

function loadSession(opportunityId: string): LocalSession {
  try {
    const raw = localStorage.getItem(SESSION_PREFIX + opportunityId)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<LocalSession>
      if (parsed?.clientId) {
        // Each map is defaulted individually: a session written by an older
        // build has a clientId and no `photos`, and spreading it over defaults
        // would still leave `photos` undefined at the first `session.photos[k]`.
        return {
          clientId: parsed.clientId,
          startedAt: parsed.startedAt ?? Date.now(),
          answers: parsed.answers ?? {},
          notes: parsed.notes ?? {},
          photos: parsed.photos ?? {},
        }
      }
    }
  } catch {
    /* Corrupt or unavailable storage starts a fresh visit rather than crashing. */
  }
  return { clientId: newId(), startedAt: Date.now(), answers: {}, notes: {}, photos: {} }
}

function saveSession(opportunityId: string, session: LocalSession): void {
  try {
    localStorage.setItem(SESSION_PREFIX + opportunityId, JSON.stringify(session))
  } catch {
    /* The visit still works in memory; only a reload would lose the row states. */
  }
}

function clearSession(opportunityId: string): void {
  try {
    localStorage.removeItem(SESSION_PREFIX + opportunityId)
  } catch {
    /* nothing to do */
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Styles — inline, because index.css belongs to another unit in this build.
 * ────────────────────────────────────────────────────────────────────────── */

const TOUCH = 56

const S = {
  page: { padding: '12px 12px 132px', maxWidth: 720, margin: '0 auto' } as const,
  head: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 } as const,
  h1: { fontSize: 20, fontWeight: 800, margin: 0 } as const,
  banner: (tone: 'ok' | 'warn' | 'info') => ({
    padding: '10px 12px', borderRadius: 'var(--radius)', fontSize: 14, marginBottom: 10,
    background: tone === 'ok' ? 'var(--score-strong-bg)' : tone === 'warn' ? 'var(--warn-bg)' : 'var(--surface)',
    color: tone === 'ok' ? 'var(--score-strong)' : tone === 'warn' ? 'var(--warn)' : 'var(--muted)',
  }) as const,
  card: {
    border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--bg)',
    padding: 12, marginBottom: 10, boxShadow: 'var(--elev-1)',
  } as const,
  itemLabel: { fontSize: 16, fontWeight: 700, lineHeight: 1.3 } as const,
  itemQuestion: { fontSize: 13, color: 'var(--muted)', marginTop: 3, lineHeight: 1.4 } as const,
  statusRow: { display: 'flex', gap: 6, marginTop: 10 } as const,
  status: (on: boolean, tone: 'pass' | 'fail' | 'unknown') => ({
    flex: 1, minHeight: TOUCH, borderRadius: 'var(--radius)', fontSize: 15, fontWeight: 800,
    letterSpacing: '.02em', cursor: 'pointer',
    border: `2px solid ${on ? 'transparent' : 'var(--line)'}`,
    background: on
      ? (tone === 'pass' ? 'var(--score-strong)' : tone === 'fail' ? 'var(--danger-solid)' : 'var(--score-weak)')
      : 'var(--surface)',
    color: on ? '#fff' : 'var(--ink)',
  }) as const,
  minor: { display: 'flex', gap: 6, marginTop: 6 } as const,
  minorBtn: (on: boolean) => ({
    minHeight: 44, padding: '0 12px', borderRadius: 'var(--radius)', fontSize: 14, fontWeight: 600,
    border: '1px solid var(--line)', background: on ? 'var(--brand-soft)' : 'var(--surface)',
    color: 'var(--ink)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  }) as const,
  bar: {
    position: 'fixed', left: 0, right: 0, bottom: 0, padding: '10px 12px calc(10px + env(safe-area-inset-bottom))',
    background: 'var(--bg)', borderTop: '1px solid var(--line)', boxShadow: 'var(--elev-2)', zIndex: 20,
  } as const,
  barRow: { display: 'flex', gap: 8, alignItems: 'center', maxWidth: 720, margin: '0 auto' } as const,
  finish: {
    flex: 1, minHeight: TOUCH, borderRadius: 'var(--radius)', border: 'none',
    background: 'var(--brand)', color: 'var(--brand-ink)', fontSize: 17, fontWeight: 800, cursor: 'pointer',
  } as const,
  count: { fontSize: 13, fontWeight: 700, color: 'var(--muted)', minWidth: 92, textAlign: 'center' } as const,
  sheet: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'end center',
    zIndex: 30, padding: 12,
  } as const,
  sheetBody: {
    width: '100%', maxWidth: 640, background: 'var(--bg)', borderRadius: 'var(--radius)', padding: 14,
    boxShadow: 'var(--elev-2)',
  } as const,
  area: {
    width: '100%', minHeight: 120, fontSize: 16, padding: 12, borderRadius: 'var(--radius)',
    border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', boxSizing: 'border-box',
  } as const,
  chip: (on: boolean) => ({
    minHeight: 44, padding: '0 14px', borderRadius: 999, fontSize: 15, fontWeight: 700,
    border: `1px solid ${on ? 'var(--brand-strong)' : 'var(--line)'}`,
    background: on ? 'var(--brand-soft)' : 'var(--surface)', color: 'var(--ink)', cursor: 'pointer',
  }) as const,
}

const VERDICTS: { value: 'buy' | 'negotiate' | 'walk' | 'undecided'; key: CopyKey }[] = [
  { value: 'buy', key: 'buy' },
  { value: 'negotiate', key: 'negotiate' },
  { value: 'walk', key: 'walk' },
  { value: 'undecided', key: 'undecided' },
]

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

export default function InspectionMode() {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])
  const route = useRoute()
  const opportunityId = route.params.id ?? ''

  const [items, setItems] = useState<DiligenceItem[]>([])
  const [heading, setHeading] = useState('')
  const [loading, setLoading] = useState(true)
  const [cachedAt, setCachedAt] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploadsOn, setUploadsOn] = useState<boolean | null>(null)
  const [durable, setDurable] = useState(true)
  const [queue, setQueue] = useState(() => outbox.getState())
  const [session, setSession] = useState<LocalSession>(() => loadSession(opportunityId))
  const [noteFor, setNoteFor] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')
  const [busyPhoto, setBusyPhoto] = useState<string | null>(null)
  const [finishing, setFinishing] = useState(false)
  const [verdict, setVerdict] = useState<'buy' | 'negotiate' | 'walk' | 'undecided'>('undecided')
  const [summary, setSummary] = useState('')
  const [finished, setFinished] = useState(false)

  const fileRef = useRef<HTMLInputElement>(null)
  const photoTarget = useRef<string | null>(null)
  // The alias the entries wait on. Derived from the visit's client id so a
  // later, second inspection of the same opportunity never inherits a stale one.
  const alias = useMemo(() => `insp_${session.clientId.replace(/[^a-zA-Z0-9_.-]/g, '')}`, [session.clientId])

  const persist = useCallback((next: LocalSession) => {
    setSession(next)
    saveSession(opportunityId, next)
  }, [opportunityId])

  useEffect(() => {
    outbox.start()
    return outbox.subscribe(() => setQueue(outbox.getState()))
  }, [])

  useEffect(() => {
    // Asked as the screen opens, which is the point §10.3 names: without it a
    // browser under storage pressure may clear the origin outright, and the
    // field is where that is unrecoverable.
    void photoQueue.requestPersistence()
    void photoQueue.durable().then(setDurable)
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const health = await get<{ uploads?: string }>('/api/health')
        if (!cancelled) setUploadsOn(health.uploads === 'on')
      } catch {
        // Unknown, not off. Being offline is not evidence the bucket is missing,
        // and refusing photos because a health check timed out would be the
        // exact wrong call in a dead spot.
        if (!cancelled) setUploadsOn(null)
      }
    })()
    return () => { cancelled = true }
  }, [])

  /** Opportunity + checklist, cached together as one §J payload. */
  const loadAll = useCallback(async () => {
    if (!opportunityId) return
    setLoading(true)
    try {
      const result = await oppCache.readThrough<{ heading: string; items: DiligenceItem[] }>(
        opportunityId,
        async () => {
          const [opp, checklist] = await Promise.all([
            get<OpportunityResponse>(`/api/opportunities/${opportunityId}`),
            get<ChecklistResponse>(`/api/opportunities/${opportunityId}/diligence`),
          ])
          return {
            heading: opp.opportunity?.title ?? opp.opportunity?.ref ?? opportunityId,
            items: checklist.items ?? [],
          }
        },
      )
      setHeading(result.payload.heading)
      setItems(result.payload.items)
      setCachedAt(result.fromCache ? result.savedAt : null)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load.')
    } finally {
      setLoading(false)
    }
  }, [opportunityId])

  useEffect(() => { void loadAll() }, [loadAll])

  /**
   * The inspection row itself. Enqueued once, lazily, on the FIRST entry —
   * opening the screen and walking away must not create an inspection, and
   * `provides` is what lets every entry name `{{alias}}` before the server has
   * ever answered.
   */
  const ensureInspection = useCallback(async () => {
    await outbox.enqueue({
      id: session.clientId,
      kind: 'inspection',
      method: 'POST',
      path: `/api/opportunities/${encodeURIComponent(opportunityId)}/inspections`,
      body: { clientId: session.clientId, deviceTime: Date.now() },
      provides: { alias, field: 'inspectionId' },
      opportunityId,
      label: 'Inspection started',
    })
  }, [alias, opportunityId, session.clientId])

  const addEntry = useCallback(async (
    body: Record<string, unknown>,
    extras: { photos?: string[]; label: string },
  ) => {
    await ensureInspection()
    const entryId = newId()
    await outbox.enqueue({
      id: entryId,
      kind: 'entry',
      method: 'POST',
      path: `/api/inspections/{{${alias}}}/entries`,
      body: { clientId: entryId, deviceTime: Date.now(), ...body },
      photos: extras.photos,
      photoField: extras.photos?.length ? 'uploadId' : undefined,
      purpose: 'inspection',
      opportunityId,
      label: extras.label,
    })
  }, [alias, ensureInspection, opportunityId])

  const setStatus = useCallback((itemKey: string, status: ItemStatus) => {
    persist({ ...session, answers: { ...session.answers, [itemKey]: status } })
    void addEntry({ itemKey, kind: 'status', status }, { label: `${itemKey}: ${status}` })
  }, [addEntry, persist, session])

  const commitNote = useCallback(async () => {
    const itemKey = noteFor
    const text = noteText.trim()
    setNoteFor(null)
    setNoteText('')
    if (!itemKey || !text) return
    persist({ ...session, notes: { ...session.notes, [itemKey]: (session.notes[itemKey] ?? 0) + 1 } })
    // The label is the item key, never the note text: §14.2 keeps free text out
    // of anything that gets listed, and the unsent queue is a list.
    await addEntry({ itemKey, kind: 'note', note: text }, { label: `${itemKey}: note` })
  }, [addEntry, noteFor, noteText, persist, session])

  const takePhoto = useCallback(async (files: FileList | null) => {
    const itemKey = photoTarget.current
    photoTarget.current = null
    const file = files?.[0]
    if (!itemKey || !file) { setBusyPhoto(null); return }
    try {
      const prepared = await prepareImage(file)
      const key = newId()
      await photoQueue.put({
        key,
        blob: prepared.blob,
        width: prepared.width,
        height: prepared.height,
        opportunityId,
        itemKey,
      })
      persist({ ...session, photos: { ...session.photos, [itemKey]: (session.photos[itemKey] ?? 0) + 1 } })
      await addEntry({ itemKey, kind: 'photo' }, { photos: [key], label: `${itemKey}: photo` })
    } catch (err) {
      const text = err instanceof ImagePrepError || err instanceof photoQueue.PhotoQueueFull ? err.message : t('photoBad')
      setError(text)
    } finally {
      setBusyPhoto(null)
    }
  }, [addEntry, opportunityId, persist, session, t])

  const finish = useCallback(async () => {
    setFinishing(true)
    await ensureInspection()
    await outbox.enqueue({
      id: `${session.clientId}:finish`,
      kind: 'finish',
      method: 'PATCH',
      path: `/api/inspections/{{${alias}}}`,
      body: { status: 'complete', verdict, ...(summary.trim() ? { summary: summary.trim() } : {}) },
      opportunityId,
      label: `Inspection finished (${verdict})`,
    })
    clearSession(opportunityId)
    setFinished(true)
    setFinishing(false)
  }, [alias, ensureInspection, opportunityId, session.clientId, summary, verdict])

  const createChecklist = useCallback(async () => {
    try {
      await post(`/api/opportunities/${opportunityId}/diligence`, {})
      oppCache.remove(opportunityId)
      await loadAll()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('needSignal'))
    }
  }, [loadAll, opportunityId, t])

  const entriesThisVisit = useMemo(() => (
    Object.keys(session.answers).length
    + Object.values(session.notes).reduce((a, b) => a + b, 0)
    + Object.values(session.photos).reduce((a, b) => a + b, 0)
  ), [session])

  const photosAllowed = uploadsOn !== false

  if (finished) {
    return (
      <div style={S.page}>
        <div style={S.banner('ok')}>{t('finished')}</div>
        {queue.pending > 0 && <div style={S.banner('warn')}>{queue.pending} {t('queued')}</div>}
        <button type="button" style={S.finish} onClick={() => navigate(`/o/${opportunityId}`)}>{t('back')}</button>
      </div>
    )
  }

  return (
    <div style={S.page}>
      <div style={S.head}>
        <h1 style={S.h1}>{t('title')}</h1>
        <span style={{ fontSize: 14, color: 'var(--muted)' }}>{heading}</span>
      </div>

      {!queue.online && <div style={S.banner('warn')}>{t('offline')}</div>}
      {cachedAt !== null && (
        <div style={S.banner('info')}>
          {t('cachedAt')} {new Date(cachedAt).toLocaleTimeString(locale === 'es-PR' ? 'es-PR' : 'en-US', { hour: '2-digit', minute: '2-digit' })} {t('offlineSuffix')}
        </div>
      )}
      {!durable && <div style={S.banner('warn')}>{t('notDurable')}</div>}
      {uploadsOn === false && <div style={S.banner('warn')}>{t('noUploads')}</div>}
      {error && <div style={S.banner('warn')}>{error}</div>}

      {loading && items.length === 0 && <p>{t('loading')}</p>}

      {!loading && items.length === 0 && (
        <div style={S.card}>
          <p style={{ marginTop: 0 }}>{t('noChecklist')}</p>
          <p style={S.itemQuestion}>{t('needSignal')}</p>
          <button type="button" style={S.minorBtn(false)} onClick={() => void createChecklist()} disabled={!queue.online}>
            {t('makeChecklist')}
          </button>
        </div>
      )}

      {items.map((item) => {
        const label = (locale === 'es-PR' ? item.label_es : item.label_en) || item.label_en
        const question = (locale === 'es-PR' ? item.question_es : item.question_en) ?? null
        // The session's answer wins over the stored one: it is what this person
        // just tapped, and the roll-up that will make it true is still in the queue.
        const chosen = session.answers[item.item_key] ?? (item.status as ItemStatus)
        const noteCount = session.notes[item.item_key] ?? 0
        const photoCount = session.photos[item.item_key] ?? 0
        return (
          <section key={item.id} style={S.card}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={S.itemLabel}>{label}</div>
                {question && <div style={S.itemQuestion}>{question}</div>}
              </div>
              {item.blocking === 1 && (
                <span style={{ ...S.chip(false), minHeight: 26, padding: '0 8px', fontSize: 11, lineHeight: '26px' }}>
                  {t('blocking')}
                </span>
              )}
            </div>

            <div style={S.statusRow}>
              <button type="button" style={S.status(chosen === 'pass', 'pass')} onClick={() => setStatus(item.item_key, 'pass')}>
                {t('pass')}
              </button>
              <button type="button" style={S.status(chosen === 'fail', 'fail')} onClick={() => setStatus(item.item_key, 'fail')}>
                {t('fail')}
              </button>
              <button type="button" style={S.status(chosen === 'unknown', 'unknown')} onClick={() => setStatus(item.item_key, 'unknown')}>
                {t('unknown')}
              </button>
            </div>

            <div style={S.minor}>
              <button type="button" style={S.minorBtn(chosen === 'na')} onClick={() => setStatus(item.item_key, 'na')}>
                {t('na')}
              </button>
              <button
                type="button"
                style={S.minorBtn(noteCount > 0)}
                onClick={() => { setNoteFor(item.item_key); setNoteText('') }}
              >
                <Icon name="info" size={16} />
                {t('note')}{noteCount > 0 ? ` (${noteCount})` : ''}
              </button>
              <button
                type="button"
                style={{ ...S.minorBtn(photoCount > 0), opacity: photosAllowed ? 1 : 0.5 }}
                disabled={!photosAllowed || busyPhoto !== null}
                onClick={() => {
                  photoTarget.current = item.item_key
                  setBusyPhoto(item.item_key)
                  fileRef.current?.click()
                }}
              >
                <Icon name="camera" size={16} />
                {busyPhoto === item.item_key ? t('photoWorking') : `${t('photo')}${photoCount > 0 ? ` (${photoCount})` : ''}`}
              </button>
            </div>
          </section>
        )
      })}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        // `capture` IS set here, unlike Intake: an inspection photo is being
        // taken right now, of the thing in front of you, and one tap fewer
        // matters when the other hand is holding a flashlight.
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          void takePhoto(e.target.files)
          e.target.value = ''
        }}
      />

      <section style={{ ...S.card, marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>{t('verdict')}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {VERDICTS.map((v) => (
            <button key={v.value} type="button" style={S.chip(verdict === v.value)} onClick={() => setVerdict(v.value)}>
              {t(v.key)}
            </button>
          ))}
        </div>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--muted)', margin: '12px 0 6px' }} htmlFor="hh-summary">
          {t('summary')}
        </label>
        <textarea id="hh-summary" style={S.area} value={summary} onChange={(e) => setSummary(e.target.value)} />
      </section>

      {noteFor !== null && (
        <div style={S.sheet} role="dialog" aria-modal="true" aria-label={t('addNote')}>
          <div style={S.sheetBody}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('addNote')}</div>
            <textarea
              style={S.area}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button type="button" style={{ ...S.finish, background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--line)' }} onClick={() => setNoteFor(null)}>
                {t('cancel')}
              </button>
              <button type="button" style={S.finish} onClick={() => void commitNote()}>{t('save')}</button>
            </div>
          </div>
        </div>
      )}

      <div style={S.bar}>
        <div style={S.barRow}>
          <span style={S.count}>
            {queue.pending > 0 ? `${queue.pending} ${t('queued')}` : t('allSent')}
          </span>
          <button type="button" style={S.finish} onClick={() => void finish()} disabled={finishing}>
            {finishing ? t('finishing') : t('finish')}
          </button>
        </div>
        <div style={{ ...S.barRow, fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
          {entriesThisVisit} {t('entries')}
          {queue.failed > 0 && <span style={{ color: 'var(--danger)', marginLeft: 8 }}>{queue.failed} failed</span>}
        </div>
      </div>
    </div>
  )
}
